import { useState, useMemo, useRef, useEffect } from 'react';
import type { Operation, Condition, Column, ColumnOption, InteractionDetails, Collections, ParsedGfql, OperatorMap } from './types.ts';

interface UseSetEditorParams {
  initialGfql: string;
  collections: Collections;
  interactionDetails: InteractionDetails;
  initialName: string;
  initialDescription: string;
  componentType: 'node' | 'edge';
  columns: Column[];
}

// Operator Maps
const GFQL_OPERATOR_MAP = {
  EQ: 'Equals',
  GT: 'Is Greater Than',
  LT: 'Is Less Than',
  GE: 'Is Greater Than or Equal',
  LE: 'Is Less Than or Equal',
  NE: 'Not Equal',
  Contains: 'Contains',
  Startswith: 'Starts With',
  Endswith: 'Ends With',
  IsIn: 'In List',
  IsNull: 'Is Null',
  NotNull: 'Is Not Null'
} as const;

const NUMBER_OPERATOR_MAP = {
  'Equals': { label: 'Equals (=)', type: 'EQ' },
  'Is Less Than': { label: 'Less Than (<)', type: 'LT' },
  'Is Greater Than': { label: 'Greater Than (>)', type: 'GT' },
  'Is Less Than or Equal': { label: 'Less Than or Equal (<=)', type: 'LE' },
  'Is Greater Than or Equal': { label: 'Greater Than or Equal (>=)', type: 'GE' },
  'Custom': { label: 'Custom (edit manually)' }
} as const;

const BOOLEAN_OPERATOR_MAP = {
  'Equals': { label: 'Equals (=)', type: 'EQ', value: (val: unknown) => val === true || val === 'true' },
  'Custom': { label: 'Custom (edit manually)' }
} as const;

export const STRING_OPERATOR_MAP: Record<string, OperatorMap> = {
  'Equals': { label: 'Equals (=)', type: 'EQ', caseSensitive: true },
  'Not Equal': { label: 'Not Equal (≠)', type: 'NE', caseSensitive: true },
  'Contains': { label: 'Contains', type: 'Contains', caseSensitive: true },
  'Starts With': { label: 'Starts With', type: 'Startswith', caseSensitive: true },
  'Ends With': { label: 'Ends With', type: 'Endswith', caseSensitive: true },
  'In List': { label: 'Is In', type: 'IsIn', caseSensitive: true },
  'Is Null': { label: 'Is Null', type: 'IsNull', hideValue: true, caseSensitive: false },
  'Is Not Null': { label: 'Is Not Null', type: 'NotNull', hideValue: true, caseSensitive: false },
  'Custom': { label: 'Custom (edit manually)' }
};

const EDGE_DIRECTION_MAP = {
  'forward': { label: 'Forward (→)', value: 'forward' },
  'reverse': { label: 'Reverse (←)', value: 'reverse' },
  'undirected': { label: 'Undirected (↔)', value: 'undirected' }
} as const;
export const DIRECTION_OPTIONS = Object.values(EDGE_DIRECTION_MAP);

const getOperatorsForType = (dataType: string): Array<{ label: string; value: string }> => Object.entries({
  number: NUMBER_OPERATOR_MAP,
  integer: NUMBER_OPERATOR_MAP,
  boolean: BOOLEAN_OPERATOR_MAP,
  bool: BOOLEAN_OPERATOR_MAP,
  string: STRING_OPERATOR_MAP
}[dataType] || STRING_OPERATOR_MAP).map(([key, value]) => ({
  label: value.label,
  value: key
}));

const getOperatorMapForType = (dataType: string): Record<string, OperatorMap> => {
  switch (dataType) {
    case 'number':
    case 'integer':
      return NUMBER_OPERATOR_MAP;
    case 'boolean':
    case 'bool':
      return BOOLEAN_OPERATOR_MAP;
    case 'string':
    default:
      return STRING_OPERATOR_MAP;
  }
};

const typecastValue = (dataType: string, value: any) => {
  switch (dataType) {
    case 'boolean':
    case 'bool':
      if (typeof value === 'boolean') return value;
      if (typeof value !== 'string') return false;

      const lower = value.toLowerCase();
      switch (lower) {
        case 'true':
        case '✓':
          return true;
        case 'false':
        case '✗':
          return false;
        default:
          return false;
      }

    case 'number':
    case 'integer':
      return Number(value);

    default:
      return value;
  }
};

const hasCustomOperators = (operations: Operation[]): boolean => {
  return operations.some(operation =>
    operation.conditions.some(condition => condition.operator === 'Custom')
  );
};

const DEFAULT_CONDITION: Condition = { operator: 'Equals', value: '', attributeName: '', caseSensitive: true };
const DEFAULT_OPERATION: Operation = { type: 'node', conditions: [DEFAULT_CONDITION] };
const DEFAULT_PARSED: ParsedGfql = { operations: [DEFAULT_OPERATION] };

const isBuilderCompatibleGfql = (gfqlString: string): boolean => {
  try {
    const parsed = JSON.parse(gfqlString);
    return Array.isArray(parsed) && parsed.every((op: any) => op?.type === 'Node' || op?.type === 'Edge');
  } catch {
    return false;
  }
};

const parseGfqlForEdit = (gfql: any): ParsedGfql => {
  try {
    if (!Array.isArray(gfql) || gfql.length === 0) return DEFAULT_PARSED;

    const operations: Operation[] = gfql.map(item => {
      const isEdge = item.type === 'Edge';
      const filterDict = isEdge ? item.edge_match : item.filter_dict;

      const conditions: Condition[] = filterDict ? Object.entries(filterDict).map(([propertyName, filterValue]: [string, any]) => {
        if (typeof filterValue === 'object' && filterValue.type) {
          const supportedKeys = ['type', 'val', 'pat', 'options', 'case'];
          const isUnsupported = Object.keys(filterValue).some(k => !supportedKeys.includes(k)) || !GFQL_OPERATOR_MAP[filterValue.type as keyof typeof GFQL_OPERATOR_MAP];
          if (isUnsupported) {
            return { operator: 'Custom', value: JSON.stringify(filterValue), attributeName: propertyName, caseSensitive: true };
          }
          const val = filterValue.val ?? filterValue.pat ?? filterValue.options;
          return {
            operator: GFQL_OPERATOR_MAP[filterValue.type as keyof typeof GFQL_OPERATOR_MAP] || 'Equals',
            value: Array.isArray(val) ? val.join(', ') : String(val ?? ''),
            attributeName: propertyName,
            caseSensitive: filterValue.case !== false
          };
        }
        return { operator: 'Equals', value: String(filterValue || ''), attributeName: propertyName, caseSensitive: true };
      }) : [];

      return {
        type: isEdge ? 'edge' : 'node',
        conditions,
        preserveEmpty: conditions.length === 0,
        ...(isEdge ? {
          direction: item.direction || 'undirected',
          hops: item.hops ?? 1,
          toFixedPoint: item.to_fixed_point ?? false
        } : {})
      } as Operation;
    });

    return operations.length > 0 ? { operations } : DEFAULT_PARSED;
  } catch {
    return DEFAULT_PARSED;
  }
};

export const useSetEditor = ({
  initialGfql,
  collections,
  interactionDetails,
  initialName,
  initialDescription,
  componentType,
  columns
}: UseSetEditorParams) => {

  // Helper: Convert to array
  const toArray = (obj: any) => Array.isArray(obj) ? obj :
    obj?.length > 0 ? Array.from({ length: obj.length }, (_, i: number) => obj[i]).filter(Boolean) : [];

  const allAvailableColumns = useMemo(() =>
    toArray(collections?.columns || interactionDetails?.columns || columns),
    [collections?.columns, interactionDetails?.columns, columns]
  );

  // Helper: Map between internal and external names
  const mapName = (name: string, toExternal = true): string => {
    if (toExternal) {
      if (name === '_id') return 'id';
      if (name === '_edgeid') return 'edgeid';
      return name;
    } else {
      if (name === 'id') return allAvailableColumns.find((c: Column) => c.name === '_id') ? '_id' : name;
      if (name === 'edgeid') return allAvailableColumns.find((c: Column) => c.name === '_edgeid') ? '_edgeid' : name;
      return name;
    }
  };

  // Helper: Find matching column
  const findMatchingColumn = useMemo(() => (attributeName: string, cols = allAvailableColumns) => {
    return cols.find((c: Column) => c && [c.key, c.name, c.attribute, (c as any).identifier].includes(attributeName) ||
      c?.name === mapName(attributeName, false) ||
      c?.name?.toLowerCase() === attributeName?.toLowerCase()
    );
  }, [allAvailableColumns]);

  // Helper: Convert GFQL to UI names
  const convertGfqlToUINames = (gfqlString: string): string => {
    try {
      const gfql = JSON.parse(gfqlString);
      if (!Array.isArray(gfql)) return gfqlString;

      const converted = gfql.map(item => {
        const isEdge = item.type === 'Edge';
        const filterDict = isEdge ? item.edge_match : item.filter_dict;
        if (!filterDict) return item;

        const newFilterDict = Object.fromEntries(
          Object.entries(filterDict).map(([name, value]) => [mapName(name), value])
        );
        return isEdge ? { ...item, edge_match: newFilterDict } : { ...item, filter_dict: newFilterDict };
      });

      return JSON.stringify(converted, null, 2);
    } catch (e) {
      return gfqlString;
    }
  };

  // Column options for dropdowns
  const columnOptions = useMemo(() => {
    const optionsMap = new Map();

    const filteredColumns = allAvailableColumns
      .filter((col: Column) => {
        if (!col) return false;
        if (!col.isInternal) return true;
        const commonInternalCols = ['_id', '_edgeid'];
        return commonInternalCols.includes(col.name || col.key);
      });

    filteredColumns.forEach((col: Column, index: number) => {
      const colKey = mapName(col.name || col.key || col.attribute);
      if (!optionsMap.has(colKey)) {
        optionsMap.set(colKey, {
          key: `${colKey}-${index}`,
          value: colKey,
          label: `${colKey} (${col.dataType})`,
          dataType: col.dataType,
          componentType: col.componentType
        });
      }
    });

    return Array.from(optionsMap.values()) as ColumnOption[];
  }, [allAvailableColumns]);

  // Parse initial GFQL
  const { initialOperations, initialAttributeName } = useMemo(() => {
    const parsed = parseGfqlForEdit(JSON.parse(initialGfql));
    const firstOperation = parsed.operations[0] || DEFAULT_OPERATION;
    const firstCondition = firstOperation.conditions[0] || DEFAULT_CONDITION;
    return {
      initialOperations: parsed.operations,
      initialAttributeName: interactionDetails?.name || firstCondition.attributeName || ''
    };
  }, [initialGfql, interactionDetails?.name]);

  // Default set name
  const defaultSetName = initialName || (interactionDetails.type === 'gfql_chain' ?
    (interactionDetails.name || interactionDetails.value ? `${interactionDetails.name} = ${interactionDetails.value}` : '') :
    interactionDetails.type === 'index_list' ? 'Multiselect Set' : 'no-op');

  // State
  const [showQueryBuilder, setShowQueryBuilder] = useState(true);
  const builderCompatible = useMemo(() => isBuilderCompatibleGfql(initialGfql), [initialGfql]);
  const [gfqlError, setGfqlError] = useState('');
  const [gfqlString, setGfqlString] = useState(initialGfql);
  const [setName, setSetName] = useState(defaultSetName);
  const [setDescription, setSetDescription] = useState(initialDescription);

  const [operations, setOperations] = useState<Operation[]>(() => {
    const isNewSet = !initialName;
    if (initialOperations?.length > 0) {
      return initialOperations.map((operation, opIndex) => ({
        type: isNewSet && opIndex === 0 ? componentType : operation.type,
        conditions: operation.conditions.map((condition, condIndex) => ({
          attributeName: condition.attributeName || (opIndex === 0 && condIndex === 0 ? initialAttributeName : ''),
          operator: condition.operator || 'Equals',
          value: condition.value || (opIndex === 0 && condIndex === 0 ? interactionDetails?.value || '' : ''),
          caseSensitive: condition.caseSensitive !== false
        })),
        preserveEmpty: operation.preserveEmpty,
        direction: operation.direction || 'undirected',
        hops: operation.hops ?? 1,
        toFixedPoint: operation.toFixedPoint ?? false
      }));
    }
    return [{
      type: componentType,
      conditions: [{ ...DEFAULT_CONDITION, attributeName: initialAttributeName || '', value: interactionDetails?.value || '' }],
      direction: 'undirected',
      hops: 1,
      toFixedPoint: false
    }];
  });

  const operatorsByDataType = useMemo(() =>
    ['number', 'integer', 'boolean', 'bool', 'string'].reduce((acc, dt) => ({
      ...acc, [dt]: getOperatorsForType(dt)
    }), {}), []);

  // Generate GFQL string from operations
  const generateGfqlString = (ops: Operation[], cols: Column[] = []): string => {
    const result: any[] = [];

    for (const operation of ops) {
      const filterDict: Record<string, any> = {};

      for (const condition of operation.conditions.filter(c => c.attributeName)) {
        const internalName = mapName(condition.attributeName, false);
        const column = findMatchingColumn(internalName, cols);
        if (!column) continue;

        let filterValue: any;
        if (condition.operator === 'Custom') {
          try { filterValue = JSON.parse(condition.value); }
          catch { filterValue = condition.value; }
        } else {
          const operatorConfig = getOperatorMapForType(column.dataType)[condition.operator];
          const opType = operatorConfig?.type;
          const addCaseFlag = column.dataType === 'string' && condition.caseSensitive === false;

          if (operatorConfig?.hideValue) {
            filterValue = { type: opType };
          } else if (condition.operator === 'In List') {
            filterValue = { type: 'IsIn', options: condition.value.split(',').map(v => v.trim()).filter(Boolean) };
          } else {
            const value = typecastValue(column.dataType, condition.value);
            if (opType === 'EQ' && !addCaseFlag) {
              filterValue = value;
            } else {
              const usesPat = ['Contains', 'Startswith', 'Endswith'].includes(opType);
              filterValue = { type: opType, [usesPat ? 'pat' : 'val']: value };
              if (addCaseFlag) filterValue.case = false;
            }
          }
        }

        filterDict[internalName] = filterValue;
      }

      if (Object.keys(filterDict).length === 0 && !operation.preserveEmpty) continue;

      if (operation.type === 'edge') {
        result.push({
          type: 'Edge',
          direction: operation.direction || 'undirected',
          hops: operation.hops ?? 1,
          to_fixed_point: operation.toFixedPoint ?? false,
          edge_match: filterDict
        });
      } else {
        result.push({
          type: 'Node',
          filter_dict: filterDict
        });
      }
    }

    return JSON.stringify(result, null, 2);
  };

  const generatedGfqlString = useMemo(() => {
    return generateGfqlString(operations, allAvailableColumns);
  }, [operations, allAvailableColumns]);

  const isInQueryBuilderMode = useRef(showQueryBuilder);
  const lastKnownGfqlString = useRef(gfqlString);

  useEffect(() => {
    isInQueryBuilderMode.current = showQueryBuilder;
  }, [showQueryBuilder]);

  useEffect(() => {
    if (builderCompatible && isInQueryBuilderMode.current && !hasCustomOperators(operations)) {
      const externalGfql = convertGfqlToUINames(generatedGfqlString);
      if (externalGfql !== lastKnownGfqlString.current) {
        setGfqlString(externalGfql);
        lastKnownGfqlString.current = externalGfql;
      }
    }
  }, [builderCompatible, generatedGfqlString, operations]);

  useEffect(() => {
    if (builderCompatible && showQueryBuilder && !hasCustomOperators(operations)) {
      const externalGfql = convertGfqlToUINames(generatedGfqlString);
      setGfqlString(externalGfql);
      lastKnownGfqlString.current = externalGfql;
    }
  }, [builderCompatible, showQueryBuilder, operations]);

  // Event handlers
  const handleGfqlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setGfqlString(value);
    lastKnownGfqlString.current = value;
    try {
      JSON.parse(value);
      setGfqlError('');
    } catch (e) {
      setGfqlError('Invalid JSON format');
    }
  };

  const handleOperationChange = (operationIndex: number, field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = field === 'hops' ? Math.max(1, parseInt(e.target.value) || 1)
      : field === 'toFixedPoint' ? (e.target as HTMLInputElement).checked
      : e.target.value;
    setOperations(prev => prev.map((operation, i) =>
      i === operationIndex ? { ...operation, [field]: value } : operation
    ));
  };

  const handleConditionChange = (operationIndex: number, conditionIndex: number, field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setOperations(prev => prev.map((operation, i) =>
      i === operationIndex ? {
        ...operation,
        conditions: operation.conditions.map((condition, j) =>
          j === conditionIndex ? { ...condition, [field]: value } : condition
        )
      } : operation
    ));
  };

  const handleAddCondition = (operationIndex: number) =>
    setOperations(prev => prev.map((operation, i) =>
      i === operationIndex ? {
        ...operation,
        conditions: [...operation.conditions, { ...DEFAULT_CONDITION }]
      } : operation
    ));

  const handleRemoveCondition = (operationIndex: number, conditionIndex: number) =>
    setOperations(prev => prev.map((operation, i) => {
      if (i !== operationIndex) return operation;
      const filtered = operation.conditions.filter((_, j) => j !== conditionIndex);
      return {
        ...operation,
        conditions: filtered.length ? filtered : [{ ...DEFAULT_CONDITION }]
      };
    }));

  const handleAddOperation = () =>
    setOperations(prev => {
      const prevOperation = prev[prev.length - 1];
      return [...prev, {
        type: prevOperation?.type || 'node',
        conditions: [{ ...DEFAULT_CONDITION }],
        direction: prevOperation?.direction || 'undirected',
        hops: prevOperation?.hops ?? 1,
        toFixedPoint: prevOperation?.toFixedPoint ?? false
      }];
    });

  const handleRemoveOperation = (operationIndex: number) =>
    setOperations(prev => {
      const filtered = prev.filter((_, i) => i !== operationIndex);
      return filtered.length ? filtered : [DEFAULT_OPERATION];
    });

  const handleCaseSensitiveChange = (operationIndex: number, conditionIndex: number, e: React.ChangeEvent<HTMLInputElement>) =>
    setOperations(prev => prev.map((operation, i) =>
      i === operationIndex ? {
        ...operation,
        conditions: operation.conditions.map((condition, j) =>
          j === conditionIndex ? { ...condition, caseSensitive: e.target.checked } : condition
        )
      } : operation
    ));

  return {
    // Data
    allAvailableColumns,
    columnOptions,
    operatorsByDataType,

    // State
    showQueryBuilder,
    setShowQueryBuilder,
    builderCompatible,
    gfqlError,
    gfqlString,
    setName,
    setSetName,
    setDescription,
    setSetDescription,
    operations,

    // Helpers
    findMatchingColumn,

    // Handlers
    handleGfqlChange,
    handleOperationChange,
    handleConditionChange,
    handleAddCondition,
    handleRemoveCondition,
    handleAddOperation,
    handleRemoveOperation,
    handleCaseSensitiveChange
  };
};
